import { Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { AccountGroupService, ProjectionFilter } from '@galaxy/account-group';
import { TranslateService } from '@ngx-translate/core';
import { ActivityInterface, WorkflowStepInterface } from '@vendasta/automata';
import { IAMService } from '@vendasta/iam';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { CommonDisplayActionService } from '../../common/common-display-action.service';
import { CommonDisplayActivityService } from '../../common/common-display-activity.service';

/**
 * Provides text for the "user associated with account group" trigger.
 */
/**
 * <h1> IMPORTANT </h1>
 *
 * Do not implement new actions, triggers, and filters using {@link CommonDisplayActionService}
 * nor {@link CommonDisplayActivityService}. These classes are deprecated, retained for backward
 * compatibility only, and <strong> are likely to be removed in the future. </strong>
 * Instead, subclass from {@link AutomationDisplayService} as this implements all modern interfaces,
 * provides convenience methods to make code more concise and readable,
 * and provides adapters to the old system while it is being phased out.
 * @see AutomationDisplayService
 * @example {@link AccountGroupCreatedService}
 */
@Injectable({
  providedIn: 'root',
})
export class UserAssociatedWithAccountGroupService implements CommonDisplayActionService, CommonDisplayActivityService {
  constructor(
    private readonly translateService: TranslateService,
    public sanitizer: DomSanitizer,
    private readonly accountGroupService: AccountGroupService,
    private readonly iamService: IAMService,
  ) {}

  getTemplateActionSubtitle(_namespace: string, _step: WorkflowStepInterface): Observable<string> {
    return of(this.translateService.instant('AUTOMATIONS.EDITOR.TRIGGERS.USER_ASSOCIATION.WHEN_ACTION'));
  }

  // Shown in the activity log (activity column) as the detail text (second line)
  // This should be past tense.
  getActivitySubtitle(_namespace: string, activity: ActivityInterface): Observable<string> {
    // Inspired by Snapshot Created trigger; you might want to update that trigger as well if problems are spotted here.
    const triggerData = activity?.triggerData ? JSON.parse(activity.triggerData) : {};
    const accountGroupId = triggerData.original_account_group_id
      ? triggerData.original_account_group_id
      : triggerData.account_group_id;
    const userId = triggerData.user_id;

    // Attempt to load the account group and user in the event.
    return combineLatest([
      this.accountGroupService.get(accountGroupId, new ProjectionFilter({ napData: true })),
      this.iamService.getMultiUsers([{ userId: userId }]).pipe(map((users) => users[0])),
    ]).pipe(
      take(1),
      map(([accountGroup, user]) => {
        // If either the user or the account group are missing, return a generic message.
        if (!user || !accountGroup) {
          return this.translateService.stream('AUTOMATIONS.EDITOR.TRIGGERS.USER_ASSOCIATION.ACTIVITY_SUBTITLE_GENERIC');
        }

        // Otherwise, get a specific message.
        return this.translateService.stream('AUTOMATIONS.EDITOR.TRIGGERS.USER_ASSOCIATION.ACTIVITY_SUBTITLE', {
          businessName: accountGroup.napData.companyName,
          userName: user.greetingName,
        });
      }),
      // If anything goes wrong fall back to the generic message.
      catchError(() => {
        return this.translateService.stream('AUTOMATIONS.EDITOR.TRIGGERS.USER_ASSOCIATION.ACTIVITY_SUBTITLE_GENERIC');
      }),
    );
  }

  // Shown in the activity log (activity column) as the summary text (first line)
  // This should be past tense.
  getActivityTitle(_namespace: string, _activity: ActivityInterface): Observable<string> {
    return of(this.translateService.instant('AUTOMATIONS.EDITOR.TRIGGERS.ENTERED_AUTOMATION'));
  }

  // This is what's shown in the automation editor and the activity log (step column, second line)
  // This should be present tense.
  getActionSubtitle(_namespace: string, _step: WorkflowStepInterface, isWaitUntil?: boolean): Observable<string> {
    let textKey = 'AUTOMATIONS.EDITOR.TRIGGERS.USER_ASSOCIATION.WHEN_ACTION';
    if (isWaitUntil) {
      textKey = 'AUTOMATIONS.EDITOR.TRIGGERS.USER_ASSOCIATION.WAIT_UNTIL_ACTION';
    }
    return of(this.translateService.instant(textKey));
  }

  getActionSubtitleEmptyState(_step: WorkflowStepInterface): Observable<string> {
    return of('');
  }

  getActionSubtitleErrorState(_params: any, _step: WorkflowStepInterface): Observable<string> {
    return of('');
  }

  getActionSubtitleSuccessState(_params: any): Observable<string> {
    return of('');
  }
}
